import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { ApiEnvNames, API_STATUS, ERROR_RESPONSE, SearchConfig } from '../../../model/api.model';
import { fetchParamsEventListForDashboard, RegistrationHeaderCard } from '../../../model/event-registration.model';
import { CommonService } from '../../../services/common.service';
import { CommonUtils } from '../../../utils/common.utils';

@Component({
  selector: 'app-event-admin',
  standalone: true,
  imports: [],
  templateUrl: './event-admin.component.html',
  styleUrl: './event-admin.component.scss'
})
export class EventAdminComponent implements OnChanges, OnInit{
  @Input() selectedEvent?: RegistrationHeaderCard;
  @Output() uploadCompleted = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput?: HTMLInputElement;

  private readonly commonService = inject(CommonService);
  private readonly commonUtils = inject(CommonUtils);

  mappingText = '{}';
  selectedUploadFile: File | null = null;
  isUploading = false;
  availableEvents: Array<RegistrationHeaderCard> = [];
  selectedEventId = '';

  ngOnInit(): void {
    if (this.selectedEvent?.event_id) {
      this.selectedEventId = this.selectedEvent.event_id;
      this.syncMappingText(this.selectedEvent);
      return;
    }
    this.fetchEventListForDashboard();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedEvent']?.currentValue?.event_id) {
      this.selectedEventId = changes['selectedEvent'].currentValue.event_id;
      this.syncMappingText(changes['selectedEvent'].currentValue);
    }
  }

  get effectiveSelectedEvent(): RegistrationHeaderCard | undefined {
    if (this.selectedEvent?.event_id) {
      return this.selectedEvent;
    }
    return this.availableEvents.find(event => String(event.event_id) === String(this.selectedEventId));
  }

  onSelectedEventChange(event: Event) {
    const selectElement = event.target as HTMLSelectElement;
    this.selectedEventId = selectElement.value;
    this.syncMappingText(this.effectiveSelectedEvent);
  }

  onMappingChanged(value: string) {
    this.mappingText = value;
  }

  onUploadFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] ?? null;
    this.selectedUploadFile = file;
  }

  uploadEventSheet = async () => {
    const event = this.effectiveSelectedEvent;

    if (this.isUploading) {
      return;
    }
    if (!event?.event_id) {
      this.commonUtils.showMatSnackBar('Please select an event before uploading.');
      return;
    }
    if (!this.selectedUploadFile) {
      this.commonUtils.showMatSnackBar('Please select an XLSX file to upload.');
      return;
    }

    let columnMapping: Record<string, string> = {};
    try {
      columnMapping = this.mappingText?.trim() ? JSON.parse(this.mappingText) : {};
    } catch (error) {
      console.info('Invalid column mapping JSON:', error);
      this.commonUtils.showMatSnackBar('Column mapping must be valid JSON.');
      return;
    }

    try {
      this.isUploading = true;
      this.commonUtils.spinner('show');
      const base64File = await this.commonUtils.readFileAsBase64(this.selectedUploadFile);


      const uploadConfig: SearchConfig = {
        method: "POST",
        path: 'uploadEventRegistrationsFromExcel',
        urlPath: ApiEnvNames['EventDashboard'],
        body: {
          eventId: event.event_id,
          eventName: event.event_name,
          replaceExisting: true,
          fileName: this.selectedUploadFile.name,
          fileContentBase64: base64File,
          columnMapping
        }
      };

      const response = await this.commonService.getDataFromDataSource(uploadConfig, ApiEnvNames['EventDashboard']);
      if (response.status === API_STATUS.Success) {
        this.commonUtils.showMatSnackBar('Event data uploaded successfully.');
        this.selectedUploadFile = null;
        if (this.fileInput) {
          this.fileInput.value = '';
        }
        this.uploadCompleted.emit();
      } else {
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE.errorFetch);
      }
    } catch (error) {
      console.info('Error uploading event registration sheet:', error);
      this.commonUtils.showMatSnackBar(ERROR_RESPONSE.errorFetch);
    } finally {
      this.isUploading = false;
      this.commonUtils.spinner('hide');
    }
  }

  private fetchEventListForDashboard() {
    this.commonUtils.spinner('show');
    this.commonService.getDataFromDataSource(fetchParamsEventListForDashboard, ApiEnvNames['EventDashboard'])
      .then((response: { status: string; result?: RegistrationHeaderCard[] }) => {
        if (response.status === API_STATUS.Success && Array.isArray(response.result)) {
          this.availableEvents = response.result;
          if (!this.selectedEventId && this.availableEvents.length > 0) {
            const defaultEvent = this.availableEvents.find(event => event.status === 'Active') ?? this.availableEvents[0];
            this.selectedEventId = String(defaultEvent.event_id);
            this.syncMappingText(defaultEvent);
          } else {
            this.syncMappingText(this.effectiveSelectedEvent);
          }
        }
      })
      .catch((error: Error) => {
        console.info('Error fetching event list for admin:', error);
      })
      .finally(() => {
        this.commonUtils.spinner('hide');
      });
  }

  private syncMappingText(event?: RegistrationHeaderCard) {
    this.mappingText = this.formatMappings(event?.mappings);
  }

  private formatMappings(mapping?: RegistrationHeaderCard['mappings']): string {
    if (!mapping) {
      return '{}';
    }

    if (typeof mapping === 'string') {
      return mapping;
    }

    return JSON.stringify(mapping, null, 2);
  }
}
